import React, { useEffect, useMemo, useState } from 'react';
import { Download, Plus, Share2, Smartphone, X } from 'lucide-react';
import './InstallAppPrompt.css';

const DISMISSED_AT_KEY = 'washMinderInstallPromptDismissedAt';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

function isRunningStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

function isIOSDevice() {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
}

function wasRecentlyDismissed() {
  const dismissedAt = Number(window.localStorage.getItem(DISMISSED_AT_KEY) || 0);
  return dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_DURATION_MS;
}

function rememberDismissal() {
  window.localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
}

export default function InstallAppPrompt({ enabled }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const [prompting, setPrompting] = useState(false);

  const isIOS = useMemo(() => isIOSDevice(), []);
  const iconUrl = `${process.env.PUBLIC_URL || ''}/apple-touch-icon.png`;

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      rememberDismissal();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  useEffect(() => {
    if (!enabled || isRunningStandalone() || wasRecentlyDismissed()) {
      setVisible(false);
      setShowIOSSteps(false);
      return;
    }

    setVisible(Boolean(deferredPrompt || isIOS));
  }, [deferredPrompt, enabled, isIOS]);

  const dismiss = () => {
    rememberDismissal();
    setVisible(false);
    setShowIOSSteps(false);
  };

  const install = async () => {
    if (!deferredPrompt) {
      setShowIOSSteps(true);
      return;
    }

    setPrompting(true);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);

      if (choice?.outcome === 'accepted') {
        rememberDismissal();
        setVisible(false);
      }
    } finally {
      setPrompting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="install-app-prompt" role="region" aria-label="Install Car Wash Minder">
      <button
        type="button"
        className="install-app-prompt__close"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
      >
        <X size={16} />
      </button>

      <img src={iconUrl} alt="" className="install-app-prompt__icon" />

      <div className="install-app-prompt__content">
        <div className="install-app-prompt__title">
          <Smartphone size={16} />
          <span>Install Car Wash Minder</span>
        </div>
        <p className="install-app-prompt__body">
          Add the dashboard to this phone for faster access.
        </p>

        {showIOSSteps && (
          <div className="install-app-prompt__steps">
            <div><Share2 size={14} /> Tap Share</div>
            <div><Plus size={14} /> Add to Home Screen</div>
          </div>
        )}

        <div className="install-app-prompt__actions">
          <button
            type="button"
            className="install-app-prompt__primary"
            onClick={install}
            disabled={prompting}
          >
            <Download size={16} />
            <span>{prompting ? 'Opening...' : isIOS ? 'Show Steps' : 'Install'}</span>
          </button>
          <button
            type="button"
            className="install-app-prompt__secondary"
            onClick={dismiss}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
